import { useCallback, useMemo, useRef } from 'react';

import { CollisionPriority } from '@dnd-kit/abstract';
import { AutoScroller, Cursor, Feedback, PreventSelection } from '@dnd-kit/dom'; // Draggable App container header
import { RestrictToElement } from '@dnd-kit/dom/modifiers';
import { arrayMove, move } from '@dnd-kit/helpers';
import { DragDropProvider, useDroppable } from '@dnd-kit/react';
import { useSortable } from '@dnd-kit/react/sortable';
import { AddOutlined, ClearOutlined, DragIndicator, SettingsOutlined } from '@mui/icons-material';
import DragIndicatorOutlinedIcon from '@mui/icons-material/DragIndicatorOutlined';
import { Box, Tab, Tabs, Tooltip } from '@mui/material';

import { ChartProvider } from '@/components/Chart/context/ChartContext'; // Draggable App container header // Draggable App container header
import { MarketWidget } from '@/components/MarketWidget';
import TooltipIconButton from '@/components/TooltipIconButton';
import Typography from '@/components/Typography';
import { useRemoveWidgetContext } from '@/context/RemoveWidgetContext';
import { NameInput } from '@/features/Dashboard/components/NameInput';
import { TABNAME_MAX_LENGTH } from '@/features/Dashboard/constants/const';
import { useEditDashboardDraft } from '@/features/Dashboard/context/EditDashboardDraft';
import { DrawerStep, useDrawerSteps } from '@/features/Dashboard/drawer/context/DrawerStepsContext';
import { useAppForm } from '@/features/Dashboard/form/AppForm/context';
import { AppType, MarketAppWithTabs, WidgetV2 } from '@/features/Dashboard/types';
import AppFormAdapter from '@/features/Dashboard/utils/AppFormAdapter'; // Draggable App container header
import { createNameSchema } from '@/features/Dashboard/utils/validateName';
import { MarketWidgetProvider } from '@/pages/Markets/MarketsComponents/contexts/MarketWidgetContext';
import { SetState } from '@/types/helpers';
import { extractIds } from '@/utils/extractIds';
import { generateUniqueUUID } from '@/utils/generateUniqueUUID';

import { reorder } from './reorder';

// Draggable App container header
const BLANK_COLUMN_ID = '__blank__';

const Item = ({
  index,
  item,
  column,
  onRemoveItem,
  container,
  isSingleItem,
  onEdit
}: {
  index: number;
  item: WidgetV2;
  column: string;
  onRemoveItem: (id: string) => void;
  onEdit: (tabId: string) => void;
  container: React.RefObject<HTMLDivElement | null>;
  isSingleItem: boolean;
}) => {
  const elementRef = useRef<HTMLDivElement | null>(null);

  const handleRef = useRef<HTMLButtonElement | null>(null);
  useSortable({
    id: item.id,
    element: elementRef,
    index,
    handle: handleRef,
    type: isSingleItem ? 'singleItem' : 'item',
    accept: 'item',
    group: column,
    modifiers: [RestrictToElement.configure({ element: container.current })]
  });
  const { openRemoveModal } = useRemoveWidgetContext();
  const { setMarketTabApps } = useEditDashboardDraft();

  const nameErrors = useMemo(() => {
    const res = createNameSchema('Name', TABNAME_MAX_LENGTH).safeParse(item.name ?? '');
    if (res.success) return undefined;
    return res.error;
  }, [item.name]);

  const isEditable =
    (item?.type === 'graph' &&
      (item.metadata?.graphType === 'forwardSimple' ||
        item.metadata?.graphType === 'seasonal' ||
        !item.metadata?.graphType)) ||
    item.type === 'table';

  return (
    <Tab
      ref={elementRef}
      disabled
      component='div'
      label={
        <>
          <Tooltip title='Drag to reorder tab'>
            <Box
              ref={handleRef}
              className='tab-drag-handle'
              sx={{
                width: 14,
                height: 46,
                p: 0,
                borderRadius: 0,
                backgroundColor: 'gx.response',
                zIndex: 5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'grab'
              }}
            >
              <DragIndicator sx={{ fontSize: '10px' }} />
            </Box>
          </Tooltip>

          <Box
            sx={{
              maxWidth: '300px',

              '.MuiTextField-root': {
                minWidth: 'auto',
                display: 'flex',
                input: {
                  color: 'gx.textDisabled',
                  '&:focus': {
                    color: 'text.primary'
                  },
                  '&:hover': {
                    color: 'text.primary'
                  }
                }
              },

              '.MuiButtonBase-root': {
                display: 'none'
              },
              '.error-message': {
                backgroundColor: 'background.paper',
                px: 0.5,
                mt: 0
              }
            }}
          >
            <NameInput
              error={nameErrors}
              isHovered={false}
              maxLength={TABNAME_MAX_LENGTH}
              value={item.name ?? ''}
              onChange={nextName => {
                setMarketTabApps(apps =>
                  apps.map(app =>
                    app.id === column
                      ? {
                          ...app,
                          tabs: app.tabs.map(t => (t.id === item.id ? { ...t, name: nextName } : t))
                        }
                      : app
                  )
                );
              }}
            />
          </Box>
          <Box
            className='edit-buttons__container'
            sx={{
              display: 'flex',
              ml: 'auto',
              gap: '2px'
            }}
          >
            {isEditable && (
              <TooltipIconButton
                sx={{ width: 14, minWidth: 14, height: 46 }}
                title='Configuration settings'
                onClick={() => {
                  onEdit(item.id);
                }}
              >
                <SettingsOutlined sx={{ fontSize: '12px', color: 'gx.textDisabled' }} />
              </TooltipIconButton>
            )}
            <TooltipIconButton
              sx={{ width: 14, minWidth: 14, height: 46 }}
              title='Remove tab'
              onClick={() => {
                openRemoveModal(() => {
                  onRemoveItem(item.id);
                });
              }}
            >
              <ClearOutlined sx={{ fontSize: '12px', color: 'gx.textDisabled' }} />
            </TooltipIconButton>
          </Box>
        </>
      }
      sx={{
        '&.Mui-disabled': {
          pointerEvents: 'auto'
        },
        overflow: 'visible',
        minWidth: '180px',
        maxWidth: 'initial',
        whiteSpace: 'nowrap',
        position: 'relative',
        textTransform: 'initial',
        opacity: '1 !important',
        height: 48,
        p: 0,
        borderWidth: '1px',
        borderStyle: 'dashed !important',
        borderColor: 'primary.main',
        alignItems: 'center',
        justifyContent: 'flex-start',
        flexDirection: 'row',
        gap: 1,
        '&:hover': {
          color: 'inherit'
        },
        outline: '0px solid transparent',
        transition: 'outline-color 120ms ease, outline-width 120ms ease',
        '&[data-tab-drop-over="true"]': {
          outlineColor: 'primary.main',
          outlineWidth: '2px'
        }
      }}
    />
  );
};
const Column = ({
  id,
  index,
  items,
  onRemoveApp,
  onRemoveItem,
  container
}: {
  id: string;
  index: number;
  items: WidgetV2[];

  onRemoveApp: (id: string) => void;
  onRemoveItem: (id: string) => void;
  container: React.RefObject<HTMLDivElement | null>;
}) => {
  const handleRef = useRef<HTMLButtonElement | null>(null);
  const { ref } = useSortable({
    id,
    index,
    handle: handleRef,
    type: 'column',
    collisionPriority: CollisionPriority.Low,
    accept: ['column', 'item', 'singleItem'],
    modifiers: [RestrictToElement.configure({ element: container.current })]
  });
  const { setDrawerStep, setIsEditMode, setEditingTarget } = useDrawerSteps();
  const { setInitialValues } = useAppForm();

  const { preview, setInsertAtId } = useEditDashboardDraft();

  const tabContent = useMemo(() => {
    const tabId = items[0]?.id;
    if (!tabId) return;

    const appWithTab = preview?.components?.find((app: any) => {
      return 'tabs' in app && app.tabs?.some((tab: any) => tab.id === tabId);
    });

    return appWithTab?.tabs?.find((tab: any) => tab.id === tabId);
  }, [preview, items]);

  const handleEdit = useCallback(
    (tabId: string) => {
      const app = items.find(app => app.id === tabId);
      if (!app) return;
      const values = AppFormAdapter.toFormValues(app);
      setIsEditMode(true);
      setEditingTarget({ appId: id, tabId });
      setInitialValues(values);
      switch (values.type) {
        case AppType.Table:
        case AppType.Tiles:
          setDrawerStep(DrawerStep.SelectBasicDataTable);
          break;
        case AppType.FWD:
          setDrawerStep(DrawerStep.SelectFWDChart);
          break;
        case AppType.Spot:
        case AppType.Seasonal:
          setDrawerStep(DrawerStep.SelectSpotChart);
          break;
        default:
          throw new Error(`Not editable app type`);
      }
    },
    [items, setIsEditMode, setEditingTarget, id, setInitialValues, setDrawerStep]
  );

  const { openRemoveModal } = useRemoveWidgetContext();

  return (
    <Box
      ref={ref}
      sx={{
        border: '4px dashed',
        borderColor: 'gx.textDisabled',
        position: 'relative',
        overflow: 'hidden',
        pt: '2px',
        backgroundColor: 'background.paper'
      }}
    >
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <TooltipIconButton
          ref={handleRef}
          sx={{ mr: 1, cursor: 'grab' }}
          title='Drag to reorder app'
        >
          <DragIndicatorOutlinedIcon />
        </TooltipIconButton>
        <Box
          sx={{
            flexGrow: 1,
            overflow: 'auto',
            display: 'flex',
            pb: 2
          }}
        >
          <Tabs
            aria-label='tabs'
            scrollButtons='auto'
            sx={{
              '& .MuiTabs-scrollButtons.Mui-disabled': {
                display: 'none'
              },
              '.name-input': {
                flexDirection: 'column'
              },
              '.error-message': {
                position: 'relative'
              }
            }}
            variant='scrollable'
          >
            {items.map((item, index) => (
              <Item
                key={item.id}
                column={id}
                container={container}
                index={index}
                isSingleItem={items.length === 1}
                item={item}
                onEdit={handleEdit}
                onRemoveItem={onRemoveItem}
              />
            ))}
          </Tabs>
          <TooltipIconButton
            title='Add new app'
            onClick={() => {
              setInsertAtId(id);
              setIsEditMode(true);
              setEditingTarget(null);
              setDrawerStep(DrawerStep.SelectApp);
            }}
          >
            <AddOutlined sx={{ fontSize: '16px' }} />
          </TooltipIconButton>
        </Box>
        <TooltipIconButton
          sx={{ ml: 1 }}
          title='Remove app'
          onClick={() => {
            openRemoveModal(() => {
              onRemoveApp(id);
            });
          }}
        >
          <ClearOutlined sx={{ fontSize: '16px' }} />
        </TooltipIconButton>
      </Box>
      <Box sx={{ flexGrow: 1, transform: 'translateY(-8px)', position: 'relative' }}>
        {tabContent && (
          <MarketWidgetProvider>
            <ChartProvider>
              <MarketWidget tab={tabContent} />
            </ChartProvider>
          </MarketWidgetProvider>
        )}
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            backgroundColor: 'rgba(18, 24, 54, 0.80)',
            zIndex: 10
          }}
        />
      </Box>
    </Box>
  );
};

const BlankColumn = () => {
  const { ref, isDropTarget } = useDroppable({
    id: BLANK_COLUMN_ID,
    type: 'new-column',
    accept: ['item'],
    collisionPriority: CollisionPriority.Low
  });

  return (
    <Box
      ref={ref}
      sx={{
        position: 'relative',
        display: 'flex',
        backgroundColor: isDropTarget ? 'rgba(53, 72, 142, 0.25)' : 'background.paper',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '40vh',
        border: '4px dashed',
        borderColor: isDropTarget ? 'gx.textDisabled' : 'transparent',
        transition: 'background-color 120ms ease, outline-color 120ms ease'
      }}
    >
      <Typography
        size={14}
        sx={{
          color: 'gx.textDisabled'
        }}
      >
        Drop a tab here
      </Typography>
    </Box>
  );
};

export type AppsOrderProps = {
  apps: MarketAppWithTabs[];
  onChange: SetState<MarketAppWithTabs[]>;
  marketData: any;
};

// AppsOrder allows: reorder apps, reorder tabs within app, move tab to another app.
export const AppsOrder = () => {
  const { marketTabApps, setMarketTabApps, setIsDragging } = useEditDashboardDraft();
  const container = useRef<HTMLDivElement | null>(null);
  const shouldShowBlankColumn = marketTabApps.some(app => app.tabs.length > 1);
  return (
    <DragDropProvider
      plugins={[Feedback, AutoScroller, Cursor, PreventSelection]}
      onDragEnd={event => {
        const operation = (event as any)?.operation;
        const { source, target } = operation ?? {};

        if (source?.type === 'item' || source?.type === 'singleItem') {
          // When dropped on the blank column, create a new app with this tab
          if (target?.id === BLANK_COLUMN_ID || target?.group === BLANK_COLUMN_ID) {
            setMarketTabApps(items => {
              const tabId = source.id as string;
              const hasTab = (app: MarketAppWithTabs) => app.tabs.some(t => t.id === tabId);
              const appContainingTab = items.find(hasTab);
              if (!appContainingTab) return items;

              // If the tab is already isolated in its own app, do nothing
              if (appContainingTab.tabs.length === 1) return items;

              const tab = appContainingTab.tabs.find(t => t.id === tabId)!;
              // Remove it from original app and create a new one
              const next = items
                .map(app => ({ ...app, tabs: app.tabs.filter(t => t.id !== tabId) }))
                .filter(app => app.tabs.length > 0);

              const appIds = extractIds(items);
              const newAppId = generateUniqueUUID(appIds);
              next.push({ id: newAppId, tabs: [tab] });
              return next;
            });
          }
        }

        // Clean up after drag ends
        setMarketTabApps(items => items.filter(app => app.tabs.length > 0));
        setIsDragging(false);
      }}
      onDragOver={event => {
        const { source, target } = event.operation as any;
        if (target?.id === undefined) return;
        if (source?.type === 'column') {
          if ((event as any).operation?.target?.id === BLANK_COLUMN_ID) {
            return;
          }
          setMarketTabApps(items => move(items, event as any));
          return;
        }

        if (source?.type === 'item' || source?.type === 'singleItem') {
          // When hovering over blank column, do not mutate items; wait for drop
          if (target?.id === BLANK_COLUMN_ID || target?.group === BLANK_COLUMN_ID) {
            return;
          }

          // Default behavior: move within/between existing apps
          setMarketTabApps(items => {
            const byApp = Object.fromEntries(items.map(app => [app.id, app.tabs]));
            const moved = reorder(byApp, event as any, arrayMove) as Record<string, WidgetV2[]>;
            return items.map(app => ({ ...app, tabs: moved[app.id] ?? [] }));
          });
        }
      }}
      onDragStart={() => {
        setIsDragging(true);
      }}
    >
      <Box
        ref={container}
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: {
            xs: '1fr',
            lg: `repeat(2, 1fr)`
          }
        }}
      >
        {marketTabApps.map((app, index) => (
          <Column
            key={app.id}
            container={container}
            id={app.id}
            index={index}
            items={app.tabs}
            onRemoveApp={removeId => {
              setMarketTabApps(items => items.filter(a => a.id !== removeId));
            }}
            onRemoveItem={removeTabId => {
              setMarketTabApps(items =>
                items
                  .map(a =>
                    a.id === app.id ? { ...a, tabs: a.tabs.filter(t => t.id !== removeTabId) } : a
                  )
                  .filter(a => a.tabs.length > 0)
              );
            }}
          />
        ))}
        {shouldShowBlankColumn && <BlankColumn />}
      </Box>
    </DragDropProvider>
  );
};

export default AppsOrder;
